// Define courseDetails as a global variable so IDEs don't complain about it being undefined.
/* global courseDetails */

import React, {createContext, useEffect} from 'react'
import ReactDOM from 'react-dom'

//import redux components
import {Provider, useDispatch, useSelector} from 'react-redux';
import store from './redux/store'
import {changeActiveCourse} from './redux/activeCourseName/activeCourseNameSlice'

//  local components
import TimeLineHero from './components/timeLineHero'
import CoursePathwayHero from './components/coursePathwayHero'
import CourseDetailsPage from './components/courseDetailsPage'
import CourseNoticeComponent from "./components/courseNotice";

// export our context for use in other components
export const Context = createContext({});

/**
 * Course details page container, this is the root entry point for the react app
 * Parameters are coming from the single course template file and attached to the
 * root element in the twig file.
 * @param {string} courseName - Currently active course name
 * @param {boolean} loggedIn - Users logged in status
 * @param {number} customerId - Customer method record id
 * @param {string} koisUrl - Base kois url
 * @param {string} cartUrl - woocommerce cart url
 * @param {string} userHighestCategory - Users highest category
 * @param {string} onlineCourseLink - Online Course Page Link
 * @param {number} showDigitalTeaserDate - The unix timestamp after which the digital teaser should be shown
 * @param brightspaceBaseUrl
 */
function CourseTemplates({
                             courseName,
                             loggedIn,
                             customerId,
                             koisUrl,
                             cartUrl,
                             userHighestCategory,
                             onlineCourseLink,
                             showDigitalTeaserDate,
                             brightspaceBaseUrl
                         }) {
    // set our global variables into a variable for context
    const value = {
        loggedIn: loggedIn,
        customerId: customerId,
        koisUrl: koisUrl,
        cartUrl: cartUrl,
        userHighestCategory: userHighestCategory,
        onlineCourseLink: onlineCourseLink,
        showDigitalTeaserDate: showDigitalTeaserDate,
        brightspaceBaseUrl: brightspaceBaseUrl,
    };

    // get the current active course name from the redux store
    const activeCourseName = useSelector((state) => state.activeCourseName.value)
    const dispatch = useDispatch()
    // All course numbers that appear in the curriculum pathway diagram
    const PATHWAY_COURSE_NUMBERS = ['165', '246', '105', '205', '305', '405', '505', '603'];

    // NOTE: courseDetails is a global variable available throughout this React app
    //       It's created in the site.php file.

    // if we are on an adjunct course, we need to dump the rest of the courses
    // from the course details object. This is mostly because we aren't going to
    // have a swiper for adjunct courses
    // EXCEPTION: If the course is part of a distinction program, we need to show that distinctions courses.
    // EXCEPTION: If the course is part of the PATHWAY_COURSE_NUMBERS, then we want to fallback to the `else` clause.
    if (!PATHWAY_COURSE_NUMBERS.includes(String(courseDetails[courseName].courseNumber).trim()) && (courseDetails[courseName].isAdjunctCourse || courseDetails[courseName].isNonCoreCourse)) {
        if (courseDetails[courseName].courseDistinctionId) {
            // if the course has a course distinction id (user category in method crm), we need to filter out the courses
            // by that id and order them by the suggested order
            let distinctionCourses = {};
            Object.entries(courseDetails).map(([key, value]) => {
                if (value.courseDistinctionId === courseDetails[courseName].courseDistinctionId) {
                    distinctionCourses[key] = value;
                }
            });
            distinctionCourses = Object.keys(distinctionCourses).sort((a, b) => distinctionCourses[a].courseDistinctionSuggestedOrder - distinctionCourses[b].courseDistinctionSuggestedOrder).reduce((obj, key) => {
                obj[key] = distinctionCourses[key];
                return obj;
            }, {});
            courseDetails = distinctionCourses;
        } else {
            courseDetails = {[courseName]: courseDetails[courseName]};
        }
    } else {
        // if this is a core course, we need to delete all the adjunct courses from our list of courses.
        let newCourseDetails = courseDetails;
        Object.entries(courseDetails).map(([key]) => {
            if (courseDetails[key].isAdjunctCourse || courseDetails[key].isNonCoreCourse) {
                const courseNum = String(courseDetails[key].courseNumber).trim();
                if (!PATHWAY_COURSE_NUMBERS.includes(courseNum)) {
                    delete newCourseDetails[key];
                }
            }
        })
        courseDetails = newCourseDetails
    }

    // the course names, helps to index the course details object
    const courseNames = Object.keys(courseDetails).sort((a, b) => {
        // if this course is part of a distinction sort the names on the course distinction suggested order
        if ((courseDetails[courseName].isAdjunctCourse || courseDetails[courseName].isNonCoreCourse) && courseDetails[courseName].courseDistinctionId) {
            return courseDetails[a].courseDistinctionSuggestedOrder - courseDetails[b].courseDistinctionSuggestedOrder
        } else {
            return courseDetails[a].courseNumber - courseDetails[b].courseNumber
        }
    });


    // Use effects ********************************************************************
    useEffect(() => {
        // update the url according to the course we are looking at
        if (activeCourseName !== "loading") {
            // Preserve the hash fragment (e.g., #digital-details) when updating the URL
            const hash = window.location.hash || '';
            history.replaceState({}, "", "/courses/" + activeCourseName + "/" + hash);
            document.title = (courseDetails[activeCourseName].courseNumber ? (courseDetails[activeCourseName].courseNumber + ': ') : '') + courseDetails[activeCourseName].title;
        }
    }, [activeCourseName]);

    useEffect(() => {
        //  the course that we are currently looking at, controls swiper and all the course data that is displaying,
        // basically an index.
        dispatch(changeActiveCourse(courseName ? courseName : courseNames[0]));
    }, []);
    // End use effects ****************************************************************

    return (
        <Context.Provider value={value}>
            {activeCourseName !== "loading" && (
                <>
                    {/* Course Notice */}
                    {courseDetails[activeCourseName].displayCourseNotice && (
                        <CourseNoticeComponent
                            title={courseDetails[activeCourseName].courseNoticeTitle}
                            message={courseDetails[activeCourseName].courseNoticeMessage}
                            type={courseDetails[activeCourseName].courseNoticeType}
                        />
                    )}

                    {(() => {
                        const currentCourseNumber = String(courseDetails[activeCourseName].courseNumber).trim();
                        const isPathwayCourse = PATHWAY_COURSE_NUMBERS.includes(currentCourseNumber);

                        return isPathwayCourse ? (
                            <CoursePathwayHero
                                courseDetailsProp={courseDetails}
                                courseNames={courseNames}
                            />
                        ) : (
                            <TimeLineHero
                                courseDetailsProp={courseDetails}
                                courseNames={courseNames}
                            />
                        );
                    })()}

                    {/* Course details section */}
                    <CourseDetailsPage
                        courseDetails={courseDetails}
                    />
                </>
            )}
        </Context.Provider>
    );
}

// entry point for the React app, this is pretty boilerplate stuff here
const appContainer = document.getElementById('course-templates-root');
if (appContainer !== null) {
    const appElement = <CourseTemplates
        courseName={appContainer.getAttribute('course-name')}
        loggedIn={appContainer.getAttribute('user-logged-in')}
        customerId={appContainer.getAttribute('customer-id')}
        koisUrl={appContainer.getAttribute('kois-url')}
        cartUrl={appContainer.getAttribute('cart-url')}
        userHighestCategory={appContainer.getAttribute('user-highest-category')}
        onlineCourseLink={appContainer.getAttribute('online_course_link')}
        showDigitalTeaserDate={appContainer.getAttribute('showDigitalTeaserDate')}
        brightspaceBaseUrl={appContainer.getAttribute('brightspace-base-url')}
    />;
    ReactDOM.render(
        <Provider store={store}>
            {appElement}
        </Provider>,
        appContainer
    );
}
